import { AddressTypeEnum } from "@lib/enum"

export const BookingCustomerSource = { POTENTIAL: 1, EXISTING: 2 } as const
export type BookingCustomerSourceValue =
  (typeof BookingCustomerSource)[keyof typeof BookingCustomerSource]

export interface IBookingCustomerAddress {
  address?: string
  provinceId?: number
  districtId?: number
  countryId?: number
  addressType: number
  isPrimary?: boolean
}

export interface IBookingCustomerLookup {
  source: BookingCustomerSourceValue
  refId: number
  customerId?: number
  existingCustomerId?: number
  code?: string
  shortName?: string
  firstName?: string
  lastName?: string
  fullName: string
  birthDate?: string
  gender?: number
  customerType?: number
  type?: number
  description?: string
  identifierCardNumber?: string
  idIssuedDate?: string
  idIssuedPlace?: string
  nationalityId?: number
  agencyMasterId?: number
  consultantId?: number
  companyName?: string
  companyAddress?: string
  companyTaxCode?: string
  position?: string
  legalRepresentative?: string
  legalRepresentativeIdIssueDate?: string
  legalRepresentativeIdentityCardNumber?: string
  legalRepresentativeIdIssuePlace?: string
  commonlyUsedBankName?: string
  bankNumber?: string
  bankBranch?: string
  bankAddress?: string
  beneficiary?: string
  bankName?: string
  bankSwiftCode?: string
  customerPhoneNumbers?: { phoneNumber: string; isPrimary: boolean }[]
  customerEmailAddresses?: { emailAddress: string; isPrimary: boolean }[]
  customerAddresses?: IBookingCustomerAddress[]
}

export interface IBookingCustomerLookupOption extends IBookingCustomerLookup {
  // `${source}-${refId}` — value duy nhất cho Select (refId KHÔNG unique giữa 2 nguồn)
  lookupKey: string
  primaryPhone?: string
  primaryEmail?: string
  currentAddress?: IBookingCustomerAddress
  permanentAddress?: IBookingCustomerAddress
  hasCurrentAddress: boolean
}

/**
 * Map DTO union (khách hàng tiềm năng + khách hàng hiện hữu) từ
 * BookingCustomerLookup/GetAll sang shape mà panel khách hàng của booking đang dùng.
 * Không tái dùng CustomerPotentialModel vì model đó derive thêm tags/areas/projects/
 * propertyTypes — union DTO không có, sẽ ra undefined gây nhiễu khi merge vào form.
 */
export class BookingCustomerLookupModel {
  public static assign(
    obj: IBookingCustomerLookup,
  ): IBookingCustomerLookupOption {
    const currentAddress = obj.customerAddresses?.find(
      (a) => a.addressType === AddressTypeEnum.CURRENT,
    )
    return {
      ...obj,
      lookupKey: `${obj.source}-${obj.refId}`,
      primaryPhone: obj.customerPhoneNumbers?.find((p) => p.isPrimary)
        ?.phoneNumber,
      primaryEmail: obj.customerEmailAddresses?.find((e) => e.isPrimary)
        ?.emailAddress,
      currentAddress,
      permanentAddress: obj.customerAddresses?.find(
        (a) => a.addressType === AddressTypeEnum.PERMANENT,
      ),
      hasCurrentAddress: !!currentAddress,
    }
  }

  public static assigns(
    objs?: IBookingCustomerLookup[],
  ): IBookingCustomerLookupOption[] {
    return (objs ?? []).map((item) => this.assign(item))
  }
}
